import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useBoardStore } from '../stores/boardStore';
import { getCheckinShareUrl } from '../api/plannerApi';
import Header from '../components/BudgetBoard/Header';
import CategoryModal, { type CatModalMode } from '../components/BudgetBoard/CategoryModal';
import AddItemModal from '../components/BudgetBoard/AddItemModal';
import BudgetBoard from '../components/BudgetBoard/Board';
import ItemEditDrawer from '../components/BudgetBoard/ItemEditDrawer';
import Stats from '../components/GuestList/Stats';
import GuestTable from '../components/GuestList/GuestTable';
import Toast, { useToast } from '../components/Toast';
import { STATUSES } from '../types/board';
import type { Category, Guest, Side, Status } from '../types/board';

type Tab = 'budget' | 'guests';

const SAVE_LABEL: Record<string, string> = {
  idle: '',
  saving: 'Saving…',
  saved: 'Saved',
  error: 'Save failed',
};

export default function PlannerPage({
  checkin = false,
  guests: checkinGuests = [],
  onToggleCheckedIn,
}: {
  checkin?: boolean;
  guests?: Guest[];
  onToggleCheckedIn?: (id: string, checkedIn: boolean) => void;
}) {
  const { token = '' } = useParams();
  const store = useBoardStore();
  const [tab, setTab] = useState<Tab>(checkin ? 'guests' : 'budget');
  const [activeCategoryId, setActiveCategoryId] = useState<string | null>(null);
  const [activeSide, setActiveSide] = useState<Side | null>(null);
  const [search, setSearch] = useState('');
  const [guestSearch, setGuestSearch] = useState('');
  const [catModal, setCatModal] = useState<{ mode: CatModalMode; category: Category | null } | null>(null);
  const [addItemStatus, setAddItemStatus] = useState<Status | null>(null);
  const [editingItemId, setEditingItemId] = useState<string | null>(null);
  const toast = useToast();

  useEffect(() => {
    if (!checkin) store.load(token);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, checkin]);

  if (!checkin && store.loading) return <div className="p-8 text-center opacity-60">Loading…</div>;
  if (!checkin && store.error) return <div className="p-8 text-center text-red-700">{store.error}</div>;

  const editingItem = store.board.items.find((i) => i.id === editingItemId) || null;
  const allGuests = checkin ? checkinGuests : store.board.guests;
  const filteredGuests = allGuests.filter((g) => {
    if (activeSide && g.side !== activeSide) return false;
    if (!guestSearch.trim()) return true;
    const q = guestSearch.toLowerCase();
    return g.name.toLowerCase().includes(q) || g.notes.toLowerCase().includes(q);
  });

  return (
    <div className="min-h-screen bg-cream text-ink">
      <header className="sticky top-0 z-50 flex flex-wrap items-center justify-between gap-x-4 gap-y-2 px-4 sm:px-8 py-4 border-b border-line bg-[oklch(99%_0.006_75_/_0.96)] backdrop-blur-[6px]">
        <div className="flex items-center gap-2.5">
          <div className="w-[26px] h-[26px] rounded-full bg-[radial-gradient(circle_at_35%_30%,oklch(80%_0.09_20),oklch(62%_0.1_20))]" />
          <span className="font-serif font-semibold text-[22px]">Wedding Planner</span>
        </div>
        <div className="flex flex-wrap items-center gap-3 sm:gap-4 text-[13px] text-muted">
          <div className="flex gap-1 bg-[oklch(94%_0.015_75)] rounded-full p-1">
            {!checkin && (
              <button
                onClick={() => setTab('budget')}
                className="border-none rounded-full px-5 py-2 text-[15px] font-semibold"
                style={{
                  background: tab === 'budget' ? 'oklch(99% 0.006 75)' : 'transparent',
                  color: tab === 'budget' ? 'oklch(24% 0.02 50)' : 'oklch(45% 0.02 50)',
                }}
              >
                Budget &amp; Milestones
              </button>
            )}
            <button
              onClick={() => !checkin && setTab('guests')}
              className="border-none rounded-full px-5 py-2 text-[15px] font-semibold"
              style={{
                background: checkin || tab === 'guests' ? 'oklch(99% 0.006 75)' : 'transparent',
                color: checkin || tab === 'guests' ? 'oklch(24% 0.02 50)' : 'oklch(45% 0.02 50)',
              }}
            >
              Guest List
            </button>
          </div>
          {!checkin && <span>{SAVE_LABEL[store.saveStatus]}</span>}
          {!checkin && (
            <button
              onClick={async () => {
                try {
                  const r = await getCheckinShareUrl(token);
                  await navigator.clipboard.writeText(r.checkinUrl);
                  toast.show('Check-in link copied!');
                } catch {
                  toast.show('Could not get check-in link.');
                }
              }}
              className="rounded-full border-[1.5px] border-terra text-terra bg-transparent px-5 py-2 text-[15px] font-semibold hover:bg-terra hover:text-cream"
            >
              Copy check-in link
            </button>
          )}
        </div>
      </header>

      <div className="max-w-[1220px] mx-auto px-6 py-8 pb-20">
        {!checkin && tab === 'budget' && (
          <>
            <Header
              board={store.board}
              onTitleChange={store.setTitle}
              activeCategoryId={activeCategoryId}
              onToggleFilter={(id) => setActiveCategoryId((cur) => (cur === id ? null : id))}
              onAddCategory={() => setCatModal({ mode: 'create', category: null })}
              onEditCategory={(cat) => setCatModal({ mode: 'rename', category: cat })}
              onDeleteCategory={(cat) => setCatModal({ mode: 'delete', category: cat })}
              search={search}
              onSearchChange={setSearch}
            />
            <BudgetBoard
              board={store.board}
              activeCategoryId={activeCategoryId}
              search={search}
              onOpenItem={setEditingItemId}
              onDropStatus={(itemId, status: Status) => store.updateItem(itemId, { status })}
              onAddItem={(status) => setAddItemStatus(status)}
            />
            {catModal && (
              <CategoryModal
                mode={catModal.mode}
                initialName={catModal.category?.name ?? ''}
                excludeId={catModal.category?.id}
                categories={store.board.categories}
                onAdd={(name) => {
                  store.addCategory(name);
                  toast.show('Category added');
                }}
                onRename={(name) => {
                  if (catModal.category) store.renameCategory(catModal.category.id, name);
                }}
                onDelete={() => {
                  if (catModal.category) store.deleteCategory(catModal.category.id);
                  toast.show('Category deleted', { label: 'Undo', onClick: store.undo });
                }}
                onClose={() => setCatModal(null)}
              />
            )}
            {addItemStatus && (
              <AddItemModal
                statusLabel={STATUSES.find((s) => s.id === addItemStatus)?.label ?? ''}
                onAdd={(title) => {
                  store.addItem(addItemStatus, title);
                  toast.show('Item added');
                }}
                onClose={() => setAddItemStatus(null)}
              />
            )}
            {editingItem && (
              <ItemEditDrawer
                item={editingItem}
                board={store.board}
                editToken={token}
                onUpdate={(patch) => store.updateItem(editingItem.id, patch)}
                onDelete={() => {
                  store.deleteItem(editingItem.id);
                  setEditingItemId(null);
                  toast.show('Item deleted', { label: 'Undo', onClick: store.undo });
                }}
                onToggleCategory={(catId) => store.toggleItemCategory(editingItem.id, catId)}
                onAddPayment={(p) => store.addPayment(editingItem.id, p)}
                onRemovePayment={(id) => {
                  store.removePayment(editingItem.id, id);
                  toast.show('Payment removed', { label: 'Undo', onClick: store.undo });
                }}
                onAddAttachment={(url) => store.addAttachment(editingItem.id, url)}
                onRemoveAttachment={(id) => {
                  store.removeAttachment(editingItem.id, id);
                  toast.show('Attachment removed', { label: 'Undo', onClick: store.undo });
                }}
                onClose={() => setEditingItemId(null)}
              />
            )}
          </>
        )}

        {tab === 'guests' && (
          <>
            <Stats
              guests={allGuests}
              activeSide={activeSide}
              onToggleSide={(s) => setActiveSide((cur) => (cur === s ? null : s))}
              search={guestSearch}
              onSearchChange={setGuestSearch}
            />
            <GuestTable
              guests={filteredGuests}
              onAdd={store.addGuest}
              onUpdate={store.updateGuest}
              onRemove={(id) => {
                store.removeGuest(id);
                toast.show('Guest removed', { label: 'Undo', onClick: store.undo });
              }}
              readOnly={checkin}
              onToggleCheckedIn={onToggleCheckedIn}
            />
          </>
        )}
      </div>

      <Toast message={toast.message} action={toast.action} />
    </div>
  );
}
